import ky from 'ky'
import type {
  GroupResponseDTO,
  CompanyDto,
  GroupedCompanyDto,
  MigrationResponseDto,
  AgentDTO,
  StatusAccountUrlResponseDto,
  BankDetailsDto,
  StatusAccountPeriodDto,
  CompanyAdministratorDto,
  CompanyAdministratorRequestDto,
  CompanyAdministratorEmailsDto,
  ColumnType,
  MembershipUsageResponseDTO,
  MembershipUsageRequestDTO,
  PreRegisterDataResultDto,
  NotificationPresetDTO,
  CustomerDTO,
  MembersData,
} from 'commons'
//import type { EmployeeDTO } from '@/types/employee-dto'
import { EmployeeDTO } from 'commons'
import { WebEnvironmentConfigService } from './env-config.service'
import type { EmailHistoryDTO } from 'commons'

export class ApiService {
  private baseUrl: string
  private envConfig: WebEnvironmentConfigService
  private headers: Record<string, string>
  private timeout: number = 60000
  private initPromise: Promise<void>

  constructor(headers?: Record<string, string>) {
    this.envConfig = new WebEnvironmentConfigService()
    this.baseUrl = 'http://localhost:3000' // Default value, will be updated in init()
    const apiKey = localStorage.getItem('apiKey')
    this.headers = {
      'Content-Type': 'application/json',
      ...(apiKey && { 'x-api-key': apiKey }),
      ...headers,
    }
    this.initPromise = this.init()
  }

  private async init(): Promise<void> {
    this.baseUrl = await this.envConfig.getApiBaseUrl()
  }

  private async ensureInitialized(): Promise<void> {
    await this.initPromise
  }

  private buildUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`
  }

  private getHeaders(): Record<string, string> {
    const apiKey = localStorage.getItem('apiKey')
    return {
      'Content-Type': 'application/json',
      ...(apiKey && { 'x-api-key': apiKey }),
    }
  }

  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    await this.ensureInitialized()
    const searchParams = params ? new URLSearchParams(params as Record<string, string>) : undefined
    return ky
      .get(this.buildUrl(endpoint), {
        headers: this.getHeaders(),
        searchParams,
      })
      .json<T>()
  }

  async post<T>(endpoint: string, data?: Record<string, unknown>): Promise<T> {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(endpoint), {
        headers: this.getHeaders(),
        json: data,
      })
      .json<T>()
  }

  async sendFile<T>(endpoint: string, file: File): Promise<T> {
    await this.ensureInitialized()
    const formData = new FormData()
    formData.append('file', file)

    const headers = this.getHeaders()
    delete headers['Content-Type']

    return ky
      .post(this.buildUrl(endpoint), {
        body: formData,
        headers,
        timeout: this.timeout,
      })
      .json<T>()
  }

  async put<T>(endpoint: string, data?: Record<string, unknown>): Promise<T> {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(endpoint), {
        headers: this.getHeaders(),
        json: data,
      })
      .json<T>()
  }

  async delete<T>(endpoint: string): Promise<T> {
    await this.ensureInitialized()
    return ky
      .delete(this.buildUrl(endpoint), {
        headers: this.getHeaders(),
      })
      .json<T>()
  }

  async validateData(endpoint: string, value: MembersData[]): Promise<MembersData[]> {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(endpoint), {
        headers: this.getHeaders(),
        json: value,
        timeout: this.timeout,
      })
      .json<MembersData[]>()
  }

  async preRegisterCustomers(
    endpoint: string,
    customers: CustomerDTO[],
    inputData?: Record<string, string>,
  ): Promise<PreRegisterDataResultDto> {
    await this.ensureInitialized()
    const inputParams = inputData ? new URLSearchParams(inputData) : undefined
    return ky
      .post(this.buildUrl(endpoint), {
        headers: this.getHeaders(),
        json: customers,
        timeout: this.timeout,
        searchParams: inputParams,
      })
      .json<PreRegisterDataResultDto>()
  }

  async getBulkLoadHistory(): Promise<GroupedCompanyDto[]> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl('/pre-register/history'), {
        headers: this.getHeaders(),
        timeout: this.timeout,
      })
      .json<GroupedCompanyDto[]>()
  }

  async getGroups(): Promise<GroupResponseDTO[]> {
    return this.get<GroupResponseDTO[]>('/groups')
  }

  async getCompanies(): Promise<CompanyDto[]> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl('/company/companies'), {
        headers: this.getHeaders(),
        timeout: this.timeout,
      })
      .json<CompanyDto[]>()
  }

  async getMembersByCompanyId(companyId: string): Promise<MembersData[]> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/pre-register/members`), {
        headers: this.getHeaders(),
        searchParams: { companyId },
        timeout: this.timeout,
      })
      .json<MembersData[]>()
  }

  async getEmployeesByGroup(groupId: string, idIsBusinessId: boolean): Promise<EmployeeDTO[]> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl('/groups-public/employees-by-group'), {
        headers: this.getHeaders(),
        searchParams: { group: groupId, idIsBusinessId: idIsBusinessId },
        timeout: this.timeout,
      })
      .json<EmployeeDTO[]>()
  }

  async getGroup(groupId: string): Promise<GroupResponseDTO> {
    return this.get<GroupResponseDTO>(`/groups-public/${groupId}`, { timeout: this.timeout })
  }

  async getAgents(): Promise<AgentDTO[]> {
    return this.get<AgentDTO[]>('/agents', { timeout: this.timeout })
  }

  async getCompanyByBusinessId(businessId: string): Promise<CompanyDto> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/company/business-id`), {
        headers: this.getHeaders(),
        searchParams: { businessId },
        timeout: this.timeout,
      })
      .json<CompanyDto>()
  }

  async getIncorporationEmployeesByGroup(groupId: string): Promise<EmployeeDTO[]> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/groups-public/incorporations-by-group`), {
        headers: this.getHeaders(),
        searchParams: { groupId },
        timeout: this.timeout,
      })
      .json<EmployeeDTO[]>()
  }

  async postReminderEmail(employeeId: string): Promise<void> {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/groups-public/reminder-email`), {
        headers: this.getHeaders(),
        searchParams: { employeeId },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async putAdminEmail(companyId: string, adminEmail: string) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/company/save-admin-email`), {
        headers: this.getHeaders(),
        searchParams: { companyId, adminEmail },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async sendAccessLinkEmail(email: string) {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/groups-public/send-access-admin-link`), {
        headers: this.getHeaders(),
        searchParams: { email },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async getRestorePasswordLink(email: string): Promise<string> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/groups-public/restore-password-link`), {
        headers: this.getHeaders(),
        searchParams: { email },
        timeout: this.timeout,
      })
      .text()
  }

  async migrateLegacyGroups(groupIds: number[]): Promise<MigrationResponseDto> {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/legacy-migration?showCreatedEmployees=true`), {
        headers: this.getHeaders(),
        json: { groupIds },
        timeout: this.timeout,
      })
      .json<MigrationResponseDto>()
  }

  async updateMemberEmail(id: string, value: string, field: ColumnType, columnName?: string) {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/pre-register/update-employee-email`), {
        headers: this.getHeaders(),
        searchParams: { id, value, field, columnName },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async getEmailHistoryByEmployeeId(employeeId: string): Promise<EmailHistoryDTO[]> {
    return this.get<EmailHistoryDTO[]>(`/email-management/email-history/${employeeId}`)
  }

  async syncDeliveryStatus(groupId: string): Promise<{ updatedCount: number }> {
    return this.post<{ updatedCount: number }>(`/email-management/sync-delivery-status/${groupId}`)
  }

  async updateCoreGroup(id: string, name: string, description: string) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/groups/update-core-group`), {
        headers: this.getHeaders(),
        searchParams: { id, name, description },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async updateAgentGroup(companyId: string, agentKey: string) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/company/update-agent-key`), {
        headers: this.getHeaders(),
        searchParams: { companyId, agentKey },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async updateIdEmpresa(companyId: string, idEmpresa: string) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/company/update-id-empresa`), {
        headers: this.getHeaders(),
        searchParams: { companyId, idEmpresa },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async updateCompanyStatus(companyId: string, status: string) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/company/update-status`), {
        headers: this.getHeaders(),
        searchParams: { companyId, status },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async sendUnsubscribeRequest(employeeId: string, reason: string) {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/groups-public/unsubscribe-request`), {
        headers: this.getHeaders(),
        searchParams: { employeeId, reason },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async getUploadPresignedUrl(
    StatusAccountPeriodDto: StatusAccountPeriodDto,
  ): Promise<StatusAccountUrlResponseDto[]> {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/account-status/generate-upload-url`), {
        // .post('https://api.osmosis.mutuus.click/account-status/generate-upload-url', {
        headers: this.getHeaders(),
        json: StatusAccountPeriodDto,
        timeout: this.timeout,
      })
      .json<StatusAccountUrlResponseDto[]>()
  }

  async getDownloadPresignedUrl(statusAccountId: string): Promise<{ url: string }> {
    await this.ensureInitialized()
    const url = await ky
      .get(this.buildUrl(`/account-status/generate-download-url`), {
        // .get('https://api.osmosis.mutuus.click/account-status/generate-download-url', {
        headers: this.getHeaders(),
        searchParams: { statusAccountId },
        timeout: this.timeout,
      })
      .text()

    return { url }
  }

  async confirmUploaded(statusAccountId: string) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/account-status/confirm-uploaded`), {
        // .put('https://api.osmosis.mutuus.click/account-status/confirm-uploaded', {
        headers: this.getHeaders(),
        searchParams: { statusAccountId },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async getUpdateUploadPresignedUrl(
    StatusAccountPeriodDto: StatusAccountPeriodDto,
  ): Promise<StatusAccountUrlResponseDto[]> {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/account-status/generate-update-upload-url`), {
        // .put('https://api.osmosis.mutuus.click/account-status/generate-update-upload-url', {
        headers: this.getHeaders(),
        json: StatusAccountPeriodDto,
        timeout: this.timeout,
      })
      .json<StatusAccountUrlResponseDto[]>()
  }

  async confirmUpdated(statusAccountId: string, filename: string, contentType: string) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/account-status/confirm-updated-file`), {
        // .put('https://api.osmosis.mutuus.click/account-status/confirm-updated-file', {
        headers: this.getHeaders(),
        searchParams: { statusAccountId, filename, contentType },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async getStatusAccountFiles(businessGroupId: string): Promise<StatusAccountPeriodDto[]> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/account-status/historic-loaded-files`), {
        // .get('https://api.osmosis.mutuus.click/account-status/historic-loaded-files', {
        headers: this.getHeaders(),
        searchParams: { businessGroupId },
        timeout: this.timeout,
      })
      .json<StatusAccountPeriodDto[]>()
  }

  async getSignedUrlForPaymentProof(
    statusAccountPeriodId: string,
    fileName: string,
    contentType: string,
  ): Promise<StatusAccountUrlResponseDto> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/account-status/generate-payment-proof-url`), {
        // .get('https://api.osmosis.mutuus.click/account-status/generate-payment-proof-url', {
        headers: this.getHeaders(),
        searchParams: { statusAccountPeriodId, fileName, contentType },
        timeout: this.timeout,
      })
      .json<StatusAccountUrlResponseDto>()
  }

  async sendBillingAccessEmail(
    businessId: string,
    month: string,
    year: string,
    paymentDeadline: string,
    isBillingEmail: boolean,
  ) {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/account-status/send-billing-access-email`), {
        headers: this.getHeaders(),
        searchParams: { businessId, month, year, paymentDeadline, isBillingEmail },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async sendVoucherSentEmail(businessId: string) {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/account-status/send-voucher-sent-email`), {
        headers: this.getHeaders(),
        searchParams: { businessId },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async getBankDetails() {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/account-status/bank-details`), {
        headers: this.getHeaders(),
        timeout: this.timeout,
      })
      .json<BankDetailsDto>()
  }

  async getUpdatePaymentProofUrl(
    statusAccountFileId: string,
    fileName: string,
    contentType: string,
  ): Promise<StatusAccountUrlResponseDto> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/account-status/generate-update-payment-proof-url`), {
        /* .get('https://api.osmosis.mutuus.click/account-status/generate-update-payment-proof-url', { */
        searchParams: { statusAccountFileId, fileName, contentType },
        timeout: this.timeout,
      })
      .json<StatusAccountUrlResponseDto>()
  }

  async addCompanyAdministrators(
    companyAdministratorRequestDto: CompanyAdministratorRequestDto,
  ): Promise<CompanyAdministratorDto[]> {
    await this.ensureInitialized()

    const payload = {
      businessId: companyAdministratorRequestDto.businessId,
      emails: companyAdministratorRequestDto.emails,
    }

    return ky
      .post(this.buildUrl(`/company-administrator`), {
        headers: this.getHeaders(),
        json: payload,
        timeout: this.timeout,
      })
      .json<CompanyAdministratorDto[]>()
  }

  async getCompanyAdministrators(businessId: string): Promise<CompanyAdministratorEmailsDto> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/company-administrator?businessId=${businessId}`), {
        headers: this.getHeaders(),
        timeout: this.timeout,
      })
      .json<CompanyAdministratorEmailsDto>()
  }

  async getMembershipUsage(
    membershipUsageRequestDTO: MembershipUsageRequestDTO,
  ): Promise<MembershipUsageResponseDTO> {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/groups-public/membership-usage`), {
        headers: this.getHeaders(),
        json: membershipUsageRequestDTO,
        timeout: this.timeout,
      })
      .json<MembershipUsageResponseDTO>()
  }

  async getTotalActiveMemberships(groupId: number): Promise<number> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/groups-public/total-active-memberships`), {
        headers: this.getHeaders(),
        searchParams: { groupId },
        timeout: this.timeout,
      })
      .json<number>()
  }

  async verifyAdminAccessTokenKey(
    tokenKey: string,
    companyId: string,
  ): Promise<{ valid: boolean }> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/groups-public/verify-admin-access-token-key`), {
        headers: this.getHeaders(),
        searchParams: { tokenKey, companyId },
        timeout: this.timeout,
      })
      .json<{ valid: boolean }>()
  }

  async disableAdminAccessTokenKey(tokenKey: string, companyId: string) {
    await this.ensureInitialized()
    return ky.put(this.buildUrl(`/groups-public/disable-admin-access-token-key`), {
      headers: this.getHeaders(),
      searchParams: { tokenKey, companyId },
      timeout: this.timeout,
    })
  }

  async getStatusAccountPeriodsLightByBusinessId(businessGroupId: string) {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/account-status/periods-light`), {
        headers: this.getHeaders(),
        searchParams: { businessGroupId },
        timeout: this.timeout,
      })
      .json<StatusAccountPeriodDto[]>()
  }

  async getNotificationTypes() {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/account-status/notification-types`), {
        headers: this.getHeaders(),
        timeout: this.timeout,
      })
      .json<[]>()
  }

  async sendDuePaymentNotification(
    periodId: string,
    notificationType: string,
    isEmailSample: boolean,
  ) {
    await this.ensureInitialized()
    return ky
      .post(this.buildUrl(`/payment-notification/send-notification`), {
        headers: this.getHeaders(),
        searchParams: { periodId, notificationType, isEmailSample },
        timeout: this.timeout,
      })
      .json<{ message: string }>()
  }

  async updatePaymentLimitDay(businessGroupId: string, paymentLimitDay: number) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/groups/update-payment-limit-day`), {
        headers: this.getHeaders(),
        searchParams: { businessGroupId, paymentLimitDay },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async getNotificationPresetByBusinessId(businessId: string): Promise<NotificationPresetDTO> {
    await this.ensureInitialized()
    return ky
      .get(this.buildUrl(`/payment-notification/notification-preset`), {
        headers: this.getHeaders(),
        searchParams: { businessId },
        timeout: this.timeout,
      })
      .json<NotificationPresetDTO>()
  }

  async deleteMember(id: string) {
    await this.ensureInitialized()
    return ky
      .delete(this.buildUrl(`/employees/delete`), {
        headers: this.getHeaders(),
        searchParams: { id },
        timeout: this.timeout,
      })
      .json<void>()
  }

  async updateEmployeeSentToMeetlife(employeeId: string, sentMeetlife: boolean) {
    await this.ensureInitialized()
    return ky
      .put(this.buildUrl(`/employees/update-sent-meetlife`), {
        headers: this.getHeaders(),
        searchParams: { employeeId, sentMeetlife },
        timeout: this.timeout,
      })
      .json<void>()
  }
}
